
import {
  CheckIcon,
  PhoneIncomingIcon,
  ChatAltIcon,
  PlayIcon,
  PlusIcon,
  PencilIcon,
} from "@vue-hero-icons/outline";
import dayjs from "dayjs";
// import { CONTANTS } from "~/constants";
const CoreDateRange = () =>
  import("~~/_core/collections/components/elements/dateTime/DateRange.vue");

export default {
  components: {
    CheckIcon,
    PhoneIncomingIcon,
    ChatAltIcon,
    PlayIcon,
    PlusIcon,
    PencilIcon,
    CoreDateRange,
  },

  layout: "default",

  fetchOnServer: false,

  head() {
    return {
      title: this.metaTitle,
      meta: [
        { hid: "title", name: "title", content: this.metaTitle },
        { hid: "description", name: "description", content: this.metaDesc },
        { hid: "image", name: "image", content: this.metaImage },
        { hid: "og:url", property: "og:url", content: this.routePath },
        { hid: "og:title", property: "og:title", content: this.metaTitle },
        { hid: "og:image", property: "og:image", content: this.metaImage },
        {
          hid: "og:image:secure_url",
          property: "og:image:secure_url",
          content: this.metaImage,
        },
        {
          hid: "og:image:secure",
          property: "og:image:secure",
          content: this.metaImage,
        },
        {
          hid: "og:image:alt",
          property: "og:image:alt",
          content: this.metaTitle,
        },
        {
          hid: "og:description",
          property: "og:description",
          content: this.metaDesc,
        },
        {
          hid: "twitter:title",
          name: "twitter:title",
          content: this.metaTitle,
        },
        {
          hid: "twitter:image:alt",
          name: "twitter:image:alt",
          content: this.metaTitle,
        },
        {
          hid: "twitter:description",
          name: "twitter:description",
          content: this.metaDesc,
        },
        {
          hid: "twitter:image",
          name: "twitter:image",
          content: this.metaImage,
        },
      ],
      link: [
        {
          hid: "canonical",
          rel: "canonical",
          href: `${this.canonical}`,
        },
      ],
      script: [
        {
          innerHTML: `{
            "@context": "http://schema.org",
            "@type": "NewsArticle",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${this.routePath}"
            },
            "headline": "${this.metaTitle}",
            "image": "${this.metaImage}",
            "datePublished": "${this.datePublished}",
            "dateModified": "${this.datePublished}",
            "author": {
              "@type": "Person",
              "name": "${this.$$siteMeta}",
              "url": "${this.routePath}"
            },
            "publisher": {
              "@type": "Organization",
              "name": "${this.$$siteMeta}",
              "logo": {
                "@type": "ImageObject",
                "url": "${this.metaImage}"
              }
            }
          }`,
          type: "application/ld+json",
        },
      ],
      __dangerouslyDisableSanitizers: ["script"],
    };
  },
  data() {
    return {
      handovers: null,
      employees: null,
      // The default value based on timzeone english or vietnamese
      model: {
        month: dayjs().month() + 1,
        range: {
          start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          end: new Date(),
        },
      },
      months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    };
  },

  computed: {
    customer() {
      return this.$get(this.$$users, "user", "");
    },

    datePublished() {
      return new Date().toISOString();
    },

    metaTitle() {
      return `${this.$t("meta.invoice.title_employee")} | ${this.$$siteMeta}`;
    },

    metaDesc() {
      return `${this.$t("employee.description")} | ${this.$$siteShortMeta}`;
    },

    metaImage() {
      return `${this.$config.protocol}://${this.$config.baseHost}/images/metacanhanf.jpeg`;
    },

    routePath() {
      return `http://${this.$config.baseHost}${this.$nuxt.context.route?.fullPath}`;
    },

    canonical() {
      return `https://${this.$config.baseHost}${this.$nuxt.context.route?.fullPath}`;
    },

    hasNoEmployeeData() {
      return this.employees == null || this.employees.length <= 0;
    },

    hasNoHandoverData() {
      return this.handovers == null || this.handovers.length <= 0;
    },
  },

  async fetch() {
    await this.fetchEmployee();
  },

  methods: {
    countHours(start, end) {
      if (!start || !end) {
        return "N/A";
      }

      const startDate = dayjs(start);
      const endDate = dayjs(end);

      const hours = endDate.diff(startDate, "hour");
      const minutes = endDate.diff(startDate, "minute");

      if (hours > 0) {
        return `${hours} ${this.$t("employee.hour")} ${
          minutes - hours * 60
        } ${this.$t("employee.minute")}`;
      }

      return `${minutes} ${this.$t("employee.minute")}`;
    },

    calculateEmployeeHours(records) {
      const result = {};

      records.forEach((record) => {
        const start = moment(record.create_date);
        const end = moment(record.end_create_date);

        if (!start.isValid() || !end.isValid()) return;

        const duration = end.diff(start, "hours", true); // true = return decimal

        if (!result[record.employee_name]) {
          result[record.employee_name] = 0;
        }

        result[record.employee_name] += duration;
      });

      return result;
    },

    async fetchHandover(startDate, endDate) {
      const { data } = await this.$axios.request({
        url: "/api/cafe/handover/",
        method: "get",
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });

      this.handovers = data?.results || [];
    },

    async onDateRangeChange($event) {
      const { start, end } = $event;

      if (!start || !end) return;

      const startDate = dayjs(start).format("YYYY-MM-DD");
      const endDate = dayjs(end).format("YYYY-MM-DD");

      await this.fetchHandover(startDate, endDate);
    },

    async changeReport($event) {
      if ($event.target.value) {
        this.model.month = $event.target.value;
      }

      const selectDate = dayjs(`${dayjs().year}-${this.model.month}-10`);
      const startDate = selectDate.startOf("month").format("YYYY-MM-DD");
      const endDate = selectDate.endOf("month").format("YYYY-MM-DD");

      await this.fetchHandover(startDate, endDate);
    },

    async fetchEmployee() {
      const { data } = await this.$axios.request({
        url: "/api/cafe/employee/",
        method: "get",
      });

      this.employees = data?.results;
    },

    editEmployee(item) {
      this.$vmodal.open({
        component: () => ({
          component: import("~/components/Modal/UpdateEmployee.vue"),
          delay: 200,
          timeout: 3000,
        }),
        props: {
          item,
          employees: this.employees,
          close: this.fetchEmployee,
        },
        wrapperProps: {
          clickCloseOutside: true,
        },
      });
    },

    async createEmployee() {
      this.$vmodal.open({
        component: () => ({
          component: import("~/components/Modal/CreateEmployee.vue"),
          delay: 200,
          timeout: 3000,
        }),
        props: {
          employees: this.employees,
          close: this.fetchEmployee,
        },
      });
    },
  },
};
